import React from 'react';
import { Box, Typography } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import type { ReceptionVisit } from '../utils/reception';

type PresenterBriefData = {
  kind: 'ai' | 'pending';
  summary: string;
  wants: string;
  facts: string[];
  latest: string;
  questions: string[];
  sourceCount: number;
};

// A visit moves between Today columns when a presenter takes it. Keep its
// completed brief in this tab so the new card can show it without reloading.
const briefsByVisit = new Map<string, PresenterBriefData>();
const rememberBrief = (visitId: string, brief: PresenterBriefData) => {
  briefsByVisit.delete(visitId);
  briefsByVisit.set(visitId, brief);
  if (briefsByVisit.size > 100) briefsByVisit.delete(briefsByVisit.keys().next().value!);
};

const actionSx = {
  border: 0, bgcolor: 'transparent', color: '#285c98', cursor: 'pointer',
  fontSize: 11.5, fontWeight: 750, px: 0.5, py: 0.5, fontFamily: 'inherit',
};

export default function PresenterBrief({ visit }: { visit: ReceptionVisit }) {
  const [brief, setBrief] = React.useState<PresenterBriefData | null>(() => briefsByVisit.get(visit.id) || null);
  const [loading, setLoading] = React.useState(() => !briefsByVisit.has(visit.id));
  const [expanded, setExpanded] = React.useState(false);
  const [unavailable, setUnavailable] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const saved = briefsByVisit.get(visit.id);
    if (saved) {
      setBrief(saved);
      setUnavailable(false);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setBrief(null);
    setUnavailable(false);
    setLoading(true);
    const load = async () => {
      try {
        const call = httpsCallable<{ visitId: string }, PresenterBriefData>(functions, 'presenterBrief');
        for (let attempt = 0; attempt < 30 && !cancelled; attempt += 1) {
          const result = await call({ visitId: visit.id });
          if (cancelled) return;
          if (result.data?.kind === 'ai') {
            rememberBrief(visit.id, result.data);
            setBrief(result.data);
            setLoading(false);
            return;
          }
          if (result.data?.kind !== 'pending') break;
          await new Promise<void>((resolve) => { setTimeout(resolve, 3000); });
        }
      } catch {
        // No recorded substitute: this panel only shows an AI-generated brief.
      }
      if (!cancelled) { setUnavailable(true); setLoading(false); }
    };
    void load();
    return () => { cancelled = true; };
  }, [visit.id]);

  return (
    <Box component="section" aria-label="Presenter brief" sx={{ border: '1px solid #d6e4f6', borderRadius: 1.25, bgcolor: '#f3f7fe', p: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <AutoAwesomeOutlinedIcon sx={{ fontSize: 15, color: '#285c98' }} />
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#244c7b', flex: 1 }}>Presenter brief</Typography>
        {brief && <Box component="button" type="button" onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded} sx={actionSx}>
          {expanded ? 'Less' : 'Details'}
        </Box>}
      </Box>
      <Typography sx={{ fontSize: 11, color: '#607087', mt: 0.2 }}>
        {loading ? 'Loading AI brief…' : unavailable ? 'AI brief unavailable right now.' : 'AI brief · saved for this visit'}
      </Typography>
      {!loading && brief && (
        <>
          <Typography sx={{ fontSize: 12, lineHeight: 1.45, color: '#243247', mt: 0.5, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: expanded ? 'unset' : 2, overflow: 'hidden', overflowWrap: 'anywhere' }}>
            {brief.summary}
          </Typography>
          {expanded && (
            <Box sx={{ display: 'grid', gap: 0.7, mt: 1, pt: 0.9, borderTop: '1px solid #dce7f5' }}>
              {brief.wants && <BriefLine label="Wants" text={brief.wants} />}
              {!!brief.facts.length && <BriefLine label="Facts" text={brief.facts.join(' · ')} />}
              {brief.latest && <BriefLine label="Latest" text={brief.latest} />}
              {!!brief.questions.length && (
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#52657f' }}>Ask</Typography>
                  {brief.questions.map((question, index) => (
                    <Typography key={index} sx={{ fontSize: 11.5, lineHeight: 1.45, color: '#243247' }}>• {question}</Typography>
                  ))}
                </Box>
              )}
              <Typography sx={{ fontSize: 10.5, color: '#667085' }}>Check the lead timeline before making a commitment.</Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function BriefLine({ label, text }: { label: string; text: string }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '46px minmax(0, 1fr)', gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#52657f' }}>{label}</Typography>
      <Typography sx={{ fontSize: 11.5, lineHeight: 1.45, color: '#243247', overflowWrap: 'anywhere' }}>{text}</Typography>
    </Box>
  );
}
